import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/auth/AuthContext'
import { ClubProvider } from '@/context/ClubContext'
import { LicenseProvider, useLicense } from '@/context/LicenseContext'
import { RequireAuth } from '@/auth/RequireAuth'
import { AppLayout } from '@/layout/AppLayout'
import { LicenseDailyModal, LicenseLockout } from '@/components/license'
import { Login } from '@/pages/Login'
import { Sso } from '@/pages/Sso'
import { SetPassword } from '@/pages/SetPassword'
import { Dashboard } from '@/pages/Dashboard'
import { Reports } from '@/pages/Reports'
import { Analytics } from '@/pages/Analytics'
import { Sales } from '@/pages/Sales'
import { Clubs } from '@/pages/Clubs'
import { Leads } from '@/pages/Leads'
import { Settings } from '@/pages/Settings'
import { Support } from '@/pages/Support'
import { Tariffs } from '@/pages/Tariffs'
import { Tables } from '@/pages/Tables'
import { Loyalty } from '@/pages/Loyalty'
import { Devices } from '@/pages/Devices'
import { Customers } from '@/pages/Customers'
import { Users } from '@/pages/Users'
import { Team } from '@/pages/Team'
import { Inventory } from '@/pages/Inventory'
import { Menu } from '@/pages/Menu'
import { Warehouse } from '@/pages/Warehouse'
import { Shifts } from '@/pages/Shifts'
import { Journal } from '@/pages/Journal'
import { Telegram } from '@/pages/Telegram'
import { Bookings } from '@/pages/Bookings'
import { Licenses } from '@/pages/Licenses'
import { Finance } from '@/pages/Finance'
import { Reconciliation } from '@/pages/Reconciliation'
import { Payroll } from '@/pages/Payroll'
import { Sms } from '@/pages/Sms'

/**
 * Каркас защищённой области. При блокировке лицензии (из /license/me или
 * «живого» 403 license_expired) полностью заменяет AppLayout экраном локаута —
 * без сайдбара и навигации. Иначе показывает обычный каркас + модалку-напоминание.
 * Супер-админ никогда не блокируется (провайдер для него пассивен).
 */
function AuthedShell(): React.JSX.Element {
  const { blocked } = useLicense()
  if (blocked) return <LicenseLockout />
  return (
    <>
      <LicenseDailyModal />
      <AppLayout />
    </>
  )
}

/**
 * Главная. Дашборд считает выручку КЛУБА, поэтому кросс-клубным ролям Help
 * (своего клуба у них нет) он отдал бы 400 — уводим каждую в её раздел.
 * Сюда же ведёт отказ RequireAuth, так что редирект обязателен: иначе роль
 * упиралась бы в бесполезный экран.
 */
function Home(): React.JSX.Element {
  const { claims } = useAuth()
  const role = claims?.role
  if (role === 'support') return <Navigate to="/clubs" replace />
  if (role === 'manager') return <Navigate to="/finance" replace />
  return <Dashboard />
}

export function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso" element={<Sso />} />
          <Route path="/set-password" element={<SetPassword />} />

          {/* Защищённая область — общий каркас с сайдбаром/хедером */}
          <Route element={<RequireAuth />}>
            <Route
              element={
                <ClubProvider>
                  <LicenseProvider>
                    <AuthedShell />
                  </LicenseProvider>
                </ClubProvider>
              }
            >
              <Route index element={<Home />} />
              {/* Поддержка доступна всем ролям: писать в неё может любой,
                  кто вошёл в бекофис, включая бухгалтера и наблюдателя. */}
              <Route path="support" element={<Support />} />

              {/* Разделы уровня КЛУБА. Кросс-клубным ролям Help закрыты: клуба у
                  них нет, и бэкенд на такие запросы отвечает 400/403. */}
              <Route element={<RequireAuth deny={['support', 'manager']} />}>
                <Route path="analytics" element={<Analytics />} />
                <Route path="reports" element={<Reports />} />
                <Route path="sales" element={<Sales />} />
                <Route path="bookings" element={<Bookings />} />
                <Route path="shifts" element={<Shifts />} />
                <Route path="journal" element={<Journal />} />
                <Route path="sms" element={<Sms />} />

                {/* Конфигурация (Фаза C) — недоступна роли «Бухгалтер» */}
                <Route element={<RequireAuth deny={['accountant']} />}>
                  <Route path="tariffs" element={<Tariffs />} />
                  <Route path="tables" element={<Tables />} />
                  <Route path="loyalty" element={<Loyalty />} />
                  <Route path="devices" element={<Devices />} />
                  <Route path="customers" element={<Customers />} />
                  <Route path="inventory" element={<Inventory />} />
                  <Route path="menu" element={<Menu />} />
                  <Route path="warehouse" element={<Warehouse />} />
                  <Route path="users" element={<Users />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="telegram" element={<Telegram />} />
                </Route>

                {/* Раздел только для владельца клуба */}
                <Route element={<RequireAuth roles={['owner']} />}>
                  <Route path="team" element={<Team />} />
                </Route>
              </Route>

              {/* Финансы: сотрудники своего клуба + кросс-клубный manager
                  (@Roles('super_admin','manager') в finance.controller). */}
              <Route element={<RequireAuth deny={['support']} />}>
                <Route path="finance" element={<Finance />} />
                <Route path="reconciliation" element={<Reconciliation />} />
                <Route path="payroll" element={<Payroll />} />
              </Route>

              {/* Разделы уровня ПЛОЩАДКИ. support ведёт клубы и заявки
                  (@Roles('super_admin','support') в clubs/leads-контроллерах);
                  лицензии остаются за супер-админом. */}
              <Route element={<RequireAuth roles={['super_admin', 'support']} />}>
                <Route path="clubs" element={<Clubs />} />
                <Route path="leads" element={<Leads />} />
              </Route>
              <Route element={<RequireAuth roles={['super_admin']} />}>
                <Route path="licenses" element={<Licenses />} />
              </Route>
            </Route>
          </Route>

          {/* fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
